/**
 * The app's auth surface is now the shared library's (DEV-5751, RBAC plan §3.6).
 *
 * `@cyngn/auth-react`'s `AuthProvider` owns the session: Discovery, the authorization-code +
 * PKCE redirect, the single coalesced refresh, the token store and the active company. This
 * module only wires it to the app's pieces — the shared `cyngn_auth.*` token store
 * (`Utils/authTokenStore`), cloud-core's post-login provisioning hop (`Utils/authOidcClient`)
 * and Faro — and re-exports `useAuth` so existing call sites keep their import.
 *
 * The role and company helpers are **UX-only**: the ID token is decoded without signature
 * verification, so `hasRole`/`hasCompany` decide what to render, never what is allowed
 * (DEV-4482; cloud-core enforces). Note the library applies the plan's role ladder in `hasRole`
 * (`Cyngn-Admin` satisfies a `Cyngn-Viewer` requirement), where the app's own provider matched
 * `cognito:groups` literally; every current call site asks for `Cyngn-Admin`, the top of the
 * ladder, so no route or control changes hands.
 *
 * The token's `cognito:groups` is the pool-wide *union* of the user's roles across companies,
 * so it cannot say which role the session holds in the company it is operating as. That comes
 * from the caller's own memberships on `users/me/` (`companies`, DEV-6432), exposed here as
 * `activeCompanyRole` next to the library's `activeCompanyId`.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useSyncExternalStore,
} from "react";
import { ReactReduxContext } from "react-redux";
import {
  AuthProvider as LibraryAuthProvider,
  useAuth as useLibraryAuth,
  type AuthContextValue as LibraryAuthContextValue,
  type CanonicalRole,
} from "@cyngn/auth-react";

import { navigateTo } from "Utils/authRedirect";
import { appAuthConfig, appOidcClient } from "Utils/authOidcClient";
import { appTokenStore } from "Utils/authTokenStore";
import { resetCognitoRetry } from "Utils/cognito";
import {
  clearTelemetryUser,
  setTelemetryUserFromStorage,
} from "Utils/faroUtils";
import { ACTIVE_COMPANY_STORAGE_KEY } from "Utils/observabilityHeaders";
import type { RootState } from "../../store";

/** Where a completed login wants to land, for the callback page to read once. */
export const POST_LOGIN_RETURN_TO_KEY = "cyngn_auth.post_login_return_to";

export interface AuthContextValue extends LibraryAuthContextValue {
  /** Every canonical role the token carries; the library keeps them on `user`. */
  roles: CanonicalRole[];
  /**
   * The canonical role the session holds in `activeCompanyId` per its `users/me/` memberships.
   * `undefined` while that is unknown (memberships not loaded, none recorded, or no active
   * company), `null` when the active company is not among the memberships at all.
   */
  activeCompanyRole: string | null | undefined;
}

/** The caller's own memberships from `users/me/`; unset until `currentUser` has loaded. */
const getCurrentUserCompanies = (
  state: RootState
): UserCompanyAssignment[] | undefined => state.currentUser.data?.companies;

const subscribeToNothing = () => () => undefined;

/**
 * `getCurrentUserCompanies` off the Redux store when one is mounted above, else unset. The
 * auth surface is also rendered without the store (its own tests, tooling), where the only
 * knowledge of roles is the token's.
 */
const useCurrentUserCompanies = (): UserCompanyAssignment[] | undefined => {
  const redux = useContext(ReactReduxContext);
  const subscribe = redux ? redux.store.subscribe : subscribeToNothing;
  const read = () =>
    redux
      ? getCurrentUserCompanies(redux.store.getState() as RootState)
      : undefined;

  return useSyncExternalStore(subscribe, read, read);
};

/** `activeCompanyRole` as documented on `AuthContextValue`. */
export const resolveActiveCompanyRole = (
  companies: UserCompanyAssignment[] | undefined,
  activeCompanyId: string | null
): string | null | undefined => {
  if (!activeCompanyId || !companies?.length) {
    return undefined;
  }
  const membership = companies.find(
    ({ companyId }) => companyId === activeCompanyId
  );

  return membership ? membership.role : null;
};

/**
 * The provider only persists the active company when the user picks one, but the request
 * header and the Faro context read it from storage on every request — including for the
 * company the session defaults to.
 */
const ActiveCompanySync = () => {
  const { activeCompanyId } = useLibraryAuth();

  useEffect(() => {
    if (activeCompanyId) {
      window.localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, activeCompanyId);
    } else {
      window.localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
    }
  }, [activeCompanyId]);

  return null;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const onLoginComplete = useCallback((returnTo: string | null) => {
    // The provider finishes the callback above the router, so the path is handed to the
    // callback page instead of navigated to from here.
    if (returnTo) {
      window.sessionStorage.setItem(POST_LOGIN_RETURN_TO_KEY, returnTo);
    }
    // The stale-session retry counter belongs to a login that failed; a completed one clears it.
    resetCognitoRetry();
    setTelemetryUserFromStorage();
  }, []);

  return (
    <LibraryAuthProvider
      config={appAuthConfig}
      client={appOidcClient}
      tokenStore={appTokenStore}
      onLoginComplete={onLoginComplete}
      navigate={navigateTo}
    >
      <ActiveCompanySync />
      {children}
    </LibraryAuthProvider>
  );
};

export const useAuth = (): AuthContextValue => {
  const value = useLibraryAuth();
  const companies = useCurrentUserCompanies();

  // Detaching the user from error reporting was part of the app provider's logout, and the
  // library knows nothing about the app's Faro instance.
  const logout = useCallback(async () => {
    clearTelemetryUser();
    await value.logout();
  }, [value]);

  return {
    ...value,
    logout,
    roles: value.user?.roles ?? [],
    activeCompanyRole: resolveActiveCompanyRole(
      companies,
      value.activeCompanyId
    ),
  };
};
